import React, { useEffect, useRef } from 'react';
import { useModalFocusTrap } from '../../hooks/useModalFocusTrap';
import { pushModalEscapeHandler } from '../../utils/modalEscapeStack';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
type ModalVariant = 'dialog' | 'sheet';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  showClose?: boolean;
  className?: string;
  footer?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  /** When false, Escape does not close (e.g. while saving). Default true. */
  closeOnEscape?: boolean;
  /**
   * Where to place focus when the dialog opens.
   * `dialog` avoids jumping into the first deep form field (important for long Manage build).
   */
  initialFocus?: 'field' | 'dialog';
}

const dialogSizeClasses: Record<ModalSize, string> = {
  sm: 'modal-panel-sm',
  md: 'modal-panel-md',
  lg: 'modal-panel-lg p-6',
  xl: 'modal-panel max-w-4xl max-h-[90vh] overflow-y-auto p-6',
};

const sheetSizeClasses: Record<ModalSize, string> = {
  sm: 'modal-sheet w-full sm:max-w-sm p-6',
  md: 'modal-sheet w-full sm:max-w-lg p-6',
  lg: 'modal-sheet w-full sm:max-w-2xl p-6',
  xl: 'modal-sheet w-full sm:max-w-4xl p-6',
};

const FIELD_FOCUS_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])';
const ANY_FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  variant = 'dialog',
  showClose = true,
  className = '',
  footer,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  initialFocus = 'field',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeOnEscapeRef = useRef(closeOnEscape);
  closeOnEscapeRef.current = closeOnEscape;

  // Focus trap still applies; initial focus target is controlled separately below.
  useModalFocusTrap(isOpen, panelRef, {
    returnFocus: true,
    skipInitialFocus: true,
  });

  // Escape stack + body scroll lock — only the topmost open modal closes on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;

    const unregister = pushModalEscapeHandler(() => {
      if (closeOnEscapeRef.current) onCloseRef.current();
    });

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      unregister();
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Initial focus once when the modal opens.
  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;

      if (initialFocus === 'dialog') {
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
        return;
      }

      const firstField = panel.querySelector<HTMLElement>(FIELD_FOCUS_SELECTOR);
      const target =
        firstField ?? panel.querySelector<HTMLElement>(ANY_FOCUSABLE_SELECTOR);

      target?.focus({ preventScroll: true });
    }, 50);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isOpen, initialFocus]);

  if (!isOpen) return null;

  const panelClasses =
    variant === 'sheet' ? sheetSizeClasses[size] : dialogSizeClasses[size];

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={description ? 'modal-description' : undefined}
        className={`${panelClasses} ${footer ? 'flex flex-col' : ''} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className="flex items-start justify-between gap-4 mb-4 sm:mb-5 shrink-0">
            <div className="min-w-0">
              {title && (
                <h2 id="modal-title" className="modal-panel-title text-xl">
                  {title}
                </h2>
              )}
              {description && (
                <p id="modal-description" className="modal-panel-description">
                  {description}
                </p>
              )}
            </div>
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className="btn-icon shrink-0 -mr-1 -mt-1"
                aria-label="Close dialog"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 flex flex-col">{children}</div>
        {footer != null && (
          <div className="sticky bottom-0 mt-5 pt-4 border-t border-white/[0.06]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
